import { useState, useEffect } from "react";
import LoginShell from "@/components/brand/LoginShell";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, QrCode, Eye, EyeOff, KeyRound, Copy, ShieldCheck } from "lucide-react";

import { formatApiErrorDetail, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { returnPath } from "@/lib/drafts";
import { useI18n } from "@/i18n/I18nContext";
import { EmailRecoveryPanel } from "@/components/control/EmailRecoveryPanel";
import { useRuntimeEnv } from "@/lib/runtimeEnv";
import {
  AuthNotice, publicInput, publicLabel, publicButton, publicSecondary,
} from "@/components/control/PublicShell";

// /login (C01/C21 credentials, C22 two-factor, C23 recovery code) in the V3 access frame.

// Pending tokens live 5 minutes; an expired or revoked one can only be renewed by signing in again.
function pendingExpired(token) {
  try {
    const { exp } = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return !exp || exp * 1000 <= Date.now();
  } catch (_) { return true; }
}

const sessionEnded = (err, token) => err.response?.status === 401
  && (pendingExpired(token) || /Sitzung/.test(String(err.response?.data?.detail || "")));

const linkCls = "text-sm font-bold text-brand-magenta hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-magenta";

function NoCodeHelp({ t }) {
  return (
    <section data-testid="recover-no-codes-help" aria-labelledby="no-codes-title"
      className="space-y-2 rounded-xl border border-[#E3DDF0] bg-[#EEEAF7] p-4 text-sm leading-relaxed text-zinc-700">
      <h2 id="no-codes-title" className="text-sm font-bold text-brand-purple">{t("login.no_codes.title")}</h2>
      <p data-testid="recover-no-codes-team">{t("login.no_codes.team")}</p>
      <p data-testid="recover-no-codes-owner">{t("login.no_codes.owner")}</p>
      <ol data-testid="recover-no-codes-owner-steps" className="list-decimal space-y-1 pl-5">
        {[1, 2, 3, 4].map((n) => <li key={n} className="break-words">{t(`login.no_codes.owner_step_${n}`)}</li>)}
      </ol>
      <p data-testid="recover-no-codes-honest" className="text-[#8A5A00]">{t("login.no_codes.honest")}</p>
    </section>
  );
}

const STEP_HEAD = {
  credentials: ["Schön, dass du für andere da bist.", "Gemeinsam sorgen wir für ein gutes Miteinander."],
  twofa: ["Zwei-Faktor-Anmeldung", "Bestätige die zweite Stufe deiner Anmeldung."],
  recover: ["Wiederherstellungscode verwenden", "Nutze einen persönlichen Wiederherstellungscode genau einmal."],
};

export default function Login() {
  const navigate = useNavigate();
  const { user, login, verify2fa, confirmSetup, recover2fa, setUser } = useAuth();
  const { t } = useI18n();

  const [step, setStep] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [pendingToken, setPendingToken] = useState("");
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [pendingUser, setPendingUser] = useState(null);
  const [codesAck, setCodesAck] = useState(false);
  const [copied, setCopied] = useState(false);
  const [resume, setResume] = useState(false);
  const [notice, setNotice] = useState("");
  // The environment badge is separate from authentication and shows only what the server confirms.
  const runtimeEnv = useRuntimeEnv();

  useEffect(() => {
    if (user) navigate(returnPath(), { replace: true });
  }, [user, navigate]);

  const submitCredentials = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const data = await login(email, password);
      setPendingToken(data.pending_token);
      setCode("");
      setResume(false);
      setNotice(data.setup_incomplete ? t("login.setup_incomplete") : "");
      if (data.setup_required) { setSetup({ qr_data_url: data.qr_data_url, secret: data.secret }); setStep("setup"); }
      else { setStep("twofa"); }
    } catch (err) {
      // 422 on login only means the Core rejected the e-mail format; never show raw English validator text.
      setError(err.response?.status === 422 ? "Bitte gib eine gültige E-Mail-Adresse ein."
        : formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const data = step === "setup"
        ? await confirmSetup(pendingToken, code)
        : await verify2fa(pendingToken, code);
      if (data.recovery_codes?.length) {
        setRecoveryCodes(data.recovery_codes);
        setPendingUser(data.user);
        setCodesAck(false);
        setStep("codes");
      } else {
        setUser(data.user);
        navigate(returnPath(), { replace: true });
      }
    } catch (err) {
      if (sessionEnded(err, pendingToken)) { endSession(); return; }
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setCode("");
    } finally { setLoading(false); }
  };

  const submitRecovery = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const data = await recover2fa(pendingToken, recoveryCode);
      setPendingToken(data.pending_token);
      setSetup({ qr_data_url: data.qr_data_url, secret: data.secret });
      setRecoveryCode(""); setCode("");
      setStep("setup");
    } catch (err) {
      if (sessionEnded(err, pendingToken)) { endSession(); return; }
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText((recoveryCodes || []).join("\n"));
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* clipboard unavailable */ }
  };

  const goBack = () => {
    setStep("credentials"); setError(""); setCode(""); setSetup(null); setRecoveryCode(""); setResume(false); setNotice("");
  };

  // Expired or revoked 5-minute session: back to the password step with a clear message, email kept.
  const endSession = () => {
    goBack(); setPendingToken(""); setError(t("login.session_expired"));
  };

  const backToCode = () => { setStep("twofa"); setError(""); setRecoveryCode(""); setCode(""); };

  // Leaving an unfinished setup keeps the (5-minute) setup session so the QR can be shown again.
  const leaveSetup = () => { setStep("credentials"); setError(""); setCode(""); setSetup(null); setResume(true); };

  const resumeSetup = async () => {
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/setup-qr", { pending_token: pendingToken });
      setSetup({ qr_data_url: data.qr_data_url, secret: data.secret }); setCode(""); setStep("setup");
    } catch (err) {
      setResume(false); setPendingToken("");
      setError(`${formatApiErrorDetail(err.response?.data?.detail) || err.message} ${t("login.setup_expired")}`);
    } finally { setLoading(false); }
  };

  const errorBox = error && (
    <p role="alert" data-testid="login-error" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-800">
      {error}
    </p>
  );
  const spinner = <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />;

  /* ---------- C01/C21 Anmeldedaten ---------- */
  const credentialsForm = (
    <form onSubmit={submitCredentials} data-testid="login-form" className="space-y-4">
      <div>
        <label htmlFor="email-r" className={publicLabel}>{t("login.email")}</label>
        <input id="email-r" type="email" autoComplete="username" required value={email} placeholder="Deine E-Mail-Adresse"
          onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" className={publicInput} />
      </div>
      <div>
        <label htmlFor="password-r" className={publicLabel}>{t("login.password")}</label>
        <div className="vl-login__password">
          <input id="password-r" type={showPassword ? "text" : "password"} autoComplete="current-password" required placeholder="Dein Passwort"
            value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" className={publicInput} />
          <button type="button" onClick={() => setShowPassword((s) => !s)} data-testid="toggle-password" aria-pressed={showPassword} aria-controls="password-r"
            aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}>
            {showPassword ? <EyeOff className="h-5 w-5" aria-hidden="true" /> : <Eye className="h-5 w-5" aria-hidden="true" />}
          </button>
        </div>
      </div>
      <Link to="/passwort-vergessen" data-testid="forgot-password-link" className="vl-login__forgot">Passwort vergessen?</Link>
      {errorBox}
      <div className="flex flex-col gap-3 sm:flex-row">
        <button type="submit" disabled={loading} data-testid="login-submit-button" className={publicButton}>
          {loading && spinner} Weiter
        </button>
      </div>
      {resume && (
        <AuthNotice testId="resume-setup-box" title={t("login.resume_setup_hint")}
          text={<button type="button" onClick={resumeSetup} disabled={loading} data-testid="resume-setup-button"
            className={`mt-1 inline-flex items-center gap-1.5 ${linkCls}`}><QrCode className="h-4 w-4" aria-hidden="true" />{t("login.resume_setup")}</button>} />
      )}
      <p className="flex items-center gap-2 text-xs text-zinc-600">
        <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />{t("login.next_hint")} {t("login.status_internal")} · {t("login.status_no_public")}
      </p>
    </form>
  );

  /* ---------- C22 Zwei-Faktor / Einrichtung ---------- */
  const otpForm = (
    <form onSubmit={submitCode} data-testid={step === "setup" ? "twofa-setup-form" : "twofa-form"} className="space-y-4">
      <AuthNotice title={step === "setup" ? t("login.setup_title") : "Persönlichen Zugang bestätigen"}
        text={step === "setup" ? t("login.setup_subtitle") : t("login.twofa_subtitle")} />
      {step === "twofa" && <p data-testid="twofa-qr-hint" className="text-xs leading-relaxed text-zinc-600">{t("login.twofa_qr_hint")}</p>}
      {step === "twofa" && notice && <AuthNotice tone="amber" testId="setup-incomplete-hint" title={notice} />}
      {step === "setup" && setup?.qr_data_url && (
        <img src={setup.qr_data_url} alt={t("login.setup_scan_hint")} data-testid="setup-qr-image"
          className="rounded-xl border border-[#E6E1F0] bg-white p-2" style={{ height: 168, width: 168 }} />
      )}
      {step === "setup" && setup?.secret && (
        <div>
          <p className="mb-1 text-xs font-semibold text-zinc-600">{t("login.setup_secret_label")}</p>
          <p data-testid="setup-secret" className="select-all break-all rounded-lg border border-[#E6E1F0] bg-white px-3 py-2 font-mono text-[13px] tracking-wider text-brand-purple">
            {setup.secret}
          </p>
        </div>
      )}
      <div>
        <label htmlFor="twofa-code" className={publicLabel}>Authenticator-Code</label>
        <input id="twofa-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} placeholder="6-stelligen Code eingeben"
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} data-testid="twofa-code-input" className={`${publicInput} tracking-widest`} />
      </div>
      {step === "twofa" && (
        <button type="button" data-testid="lost-authenticator-link" onClick={() => { setStep("recover"); setError(""); setCode(""); }} className={`block ${linkCls}`}>
          Wiederherstellungscode verwenden
        </button>
      )}
      {errorBox}
      <div className="flex flex-col gap-3 sm:flex-row">
        <button type="submit" disabled={loading || code.length < 6} data-testid={step === "setup" ? "setup-confirm-button" : "twofa-submit-button"} className={publicButton}>
          {loading && spinner} {step === "setup" ? t("login.setup_confirm") : "Code bestätigen"}
        </button>
        <button type="button" onClick={step === "setup" ? leaveSetup : goBack} data-testid="twofa-back" className={publicSecondary}>Zurück</button>
      </div>
    </form>
  );

  /* ---------- C23 Wiederherstellung per Code ---------- */
  const startMailSetup = (data) => {
    setPendingToken(data.pending_token);
    setSetup({ qr_data_url: data.qr_data_url, secret: data.secret });
    setRecoveryCode(""); setCode(""); setError("");
    setStep("setup");
  };

  const recoverForm = (
    <div className="space-y-4">
      <form onSubmit={submitRecovery} data-testid="recover-form" className="space-y-4">
        <AuthNotice title="Persönlichen Zugang bestätigen" text={t("login.recover_subtitle")} />
        <div>
          <label htmlFor="recovery-code" className={publicLabel}>Wiederherstellungscode</label>
          <input id="recovery-code" type="text" autoComplete="one-time-code" required value={recoveryCode} onChange={(e) => setRecoveryCode(e.target.value)}
            data-testid="recovery-code-input" placeholder="XXXXX-XXXXX-XXXXX-XXXXX" className={`${publicInput} font-mono tracking-wider`} />
        </div>
        {errorBox}
        <div className="flex flex-col gap-3 sm:flex-row">
          <button type="submit" disabled={loading || recoveryCode.replace(/[\s-]/g, "").length < 12} data-testid="recover-submit-button" className={publicButton}>
            {loading && spinner} {t("login.recover_submit")}
          </button>
          <button type="button" onClick={goBack} data-testid="recover-back" className={publicSecondary}>Zurück</button>
        </div>
        <button type="button" onClick={backToCode} data-testid="recover-back-to-code" className={`block ${linkCls}`}>{t("login.recover_back_to_code")}</button>
      </form>
      <NoCodeHelp t={t} />
      <EmailRecoveryPanel pendingToken={pendingToken} onSetup={startMailSetup} />
    </div>
  );

  /* ---------- Wiederherstellungscodes (einmalige Anzeige) ---------- */
  const codesForm = (
    <div data-testid="recovery-codes-view" className="space-y-4">
      <AuthNotice tone="amber" title={t("login.codes_title")} text={t("login.codes_subtitle")} />
      <ol data-testid="recovery-codes-list" className="grid grid-cols-1 gap-2 rounded-xl border border-[#E6E1F0] bg-white p-4 font-mono text-[15px] text-brand-purple sm:grid-cols-2">
        {(recoveryCodes || []).map((c, i) => (
          <li key={i} data-testid={`recovery-code-${i}`} className="select-all tracking-wider">{(c.match(/.{1,5}/g) || [c]).join("-")}</li>
        ))}
      </ol>
      <button type="button" onClick={copyCodes} data-testid="copy-codes-button" className={publicSecondary}>
        <Copy className="h-4 w-4" aria-hidden="true" />{copied ? t("login.codes_copied") : t("login.codes_copy")}
      </button>
      <label className="flex items-start gap-2 text-sm text-zinc-700">
        <input type="checkbox" checked={codesAck} onChange={(e) => setCodesAck(e.target.checked)} data-testid="codes-ack-checkbox" className="mt-0.5 h-4 w-4 accent-[#D50B58]" />
        <span>{t("login.codes_ack")}</span>
      </label>
      <button type="button" disabled={!codesAck} data-testid="codes-continue-button" onClick={() => { setUser(pendingUser); navigate(returnPath(), { replace: true }); }} className={publicButton}>
        <KeyRound className="h-4 w-4" aria-hidden="true" />{t("login.codes_continue")}
      </button>
    </div>
  );

  const known = runtimeEnv.status !== "unknown";
  const topRight = (
    <>
      {runtimeEnv.status !== "loading" && (
        <span data-testid="env-badge" data-env-status={runtimeEnv.status}
          className={`rounded-full px-3 py-1 text-xs font-semibold ${known ? "bg-[#FDE7F0] text-brand-magenta" : "bg-zinc-100 text-zinc-600"}`}>
          {t(runtimeEnv.labelKey)}
        </span>
      )}
    </>
  );
  const [title, subtitle] = STEP_HEAD[step] || (step === "setup"
    ? [t("login.setup_title"), t("login.setup_subtitle")] : [t("login.codes_title"), ""]);

  return (
    <LoginShell variant="control" title={title} subtitle={subtitle} testId="login-card" headerExtras={topRight}>
      {step === "credentials" ? credentialsForm : step === "recover" ? recoverForm : step === "codes" ? codesForm : otpForm}
    </LoginShell>
  );
}
